/**
 * Achievements Tab Component
 * 
 * Main container component that manages achievement data fetching and rendering.
 * Uses accordion-based UI where achievements load on-demand when a game is expanded.
 * 
 */

import React, { useEffect, useState, useMemo } from 'react';

import { api } from '../../../../services/api';
import { SkeletonContainer } from '../../../../components/Skeleton';
import { StatisticsHeader } from './StatisticsHeader';
import { GameAccordion } from './GameAccordion';
import SyncButton from '../../../../components/SyncButton';
import SyncStatusMessage from '../../../../components/SyncStatusMessage';
import SyncProgress from '../../../../components/SyncProgress';
import EmptyState from '../../../../components/EmptyState';
import { AchievementsTabSkeleton } from '../components/AchievementsTabSkeleton';

import { Profile } from '../../../../types/user';
import {
  AchievementStatsResponse,
} from '../../../../types/achievements';

import styles from './Achievements.module.scss';

/**
 * Props for Achievements component
 * 
 */
export interface AchievementsProps {
  profile: Profile;
}

/**
 * Cache key prefix for sessionStorage
 */
const CACHE_KEY_PREFIX = 'achievements_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Validates UUID format
 * 
 */
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Main Achievements Tab Component
 * 
 */
export const Achievements: React.FC<AchievementsProps> = ({ profile }) => {
  // Extract userId from profile
  const userId = profile?.user?.userId;

  // Data state
  const [stats, setStats] = useState<AchievementStatsResponse | null>(null);
  const [gameNames, setGameNames] = useState<Record<string, string>>({});
  const [gameCoverUrls, setGameCoverUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState<string>('');

  // UI state
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [syncJobInProgress, setSyncJobInProgress] = useState<boolean>(false);

  /**
   * Check sync status on mount and periodically
   */
  useEffect(() => {
    if (!profile.isMe || !userId) return;

    const checkSyncStatus = async () => {
      try {
        const status = await api.app.getSyncStatus();
        const achievementsSyncInProgress = status.jobs.some(
          (job: any) => job.jobType === "achievements" && job.status === "in_progress"
        );
        setSyncJobInProgress(achievementsSyncInProgress);
        if (achievementsSyncInProgress) {
          setSyncing(true);
        }
      } catch (err) {
        console.error("Error checking sync status:", err);
      }
    };

    checkSyncStatus();

    // Poll every 5 seconds if sync is in progress
    const interval = setInterval(() => {
      if (syncJobInProgress || syncing) {
        checkSyncStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [profile.isMe, userId, syncJobInProgress, syncing]);

  /**
   * Get cache key for user
   */
  const getCacheKey = (uid: string) => `${CACHE_KEY_PREFIX}${uid}`;
  const getCacheTimestampKey = (uid: string) => `${CACHE_KEY_PREFIX}${uid}_timestamp`;

  /**
   * Check if cached data is still valid
   * 
   */
  const getCachedData = (uid: string): AchievementStatsResponse | null => {
    try {
      const cacheKey = getCacheKey(uid);
      const timestampKey = getCacheTimestampKey(uid);
      
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(timestampKey);

      if (cachedData && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp, 10);
        if (age < CACHE_TTL) {
          return JSON.parse(cachedData);
        }
      }
    } catch (err) {
      console.warn('Failed to read cache:', err);
    }
    return null;
  };

  /**
   * Store data in cache
   * 
   */
  const setCachedData = (uid: string, data: AchievementStatsResponse) => {
    try {
      const cacheKey = getCacheKey(uid);
      const timestampKey = getCacheTimestampKey(uid);
      
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
      sessionStorage.setItem(timestampKey, Date.now().toString());
    } catch (err) {
      console.warn('Failed to write cache:', err);
    }
  };

  /**
   * Clear cache for user
   */
  const clearCache = (uid: string) => {
    try {
      sessionStorage.removeItem(getCacheKey(uid));
      sessionStorage.removeItem(getCacheTimestampKey(uid));
    } catch (err) {
      console.warn('Failed to clear cache:', err);
    }
  };

  /**
   * Fetch game names and cover URLs for display
   * 
   */
  const fetchGameNames = async () => {
    if (!userId) {
      return;
    }

    try {
      const gamesResponse = await api.achievements.getUserGames(userId);
      
      // Build gameNames and coverUrls lookup maps
      const gameNamesMap = gamesResponse.games.reduce((acc, game) => {
        acc[game.gameId] = game.name;
        return acc;
      }, {} as Record<string, string>);
      
      const gameCoverUrlsMap = gamesResponse.games.reduce((acc, game) => {
        if (game.coverUrl) {
          acc[game.gameId] = game.coverUrl;
        }
        return acc;
      }, {} as Record<string, string>);
      
      setGameNames(gameNamesMap);
      setGameCoverUrls(gameCoverUrlsMap);
    } catch (err) {
      console.warn('Failed to fetch game names:', err);
      // Non-critical error - continue with gameId fallback
    }
  };

  /**
   * Fetch achievement statistics
   * 
   */
  const fetchAchievementStats = async (useCache = true) => {
    if (!userId) {
      setError('User ID is missing');
      setLoading(false);
      return;
    }

    // Validate userId format
    if (!isValidUUID(userId)) {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check cache first
      if (useCache) {
        const cachedStats = getCachedData(userId);
        if (cachedStats) {
          setStats(cachedStats);
          // Fetch game names
          await fetchGameNames();
          setLoading(false);
          return;
        }
      }

      // Fetch fresh data from API
      const statsData = await api.achievements.getUserAchievementStats(userId);
      setStats(statsData);
      
      // Cache the stats data
      setCachedData(userId, statsData);

      // Fetch game names for display
      await fetchGameNames();

      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch achievement stats:', err);
      
      // Handle specific error cases
      if (err.status === 401) {
        setError('Please log in to view achievements');
      } else if (err.status === 403) {
        setError('This user\'s achievements are private');
      } else if (err.status === 404) {
        setError('User not found');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to load achievement data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle manual sync/refresh
   * 
   */
  const handleSync = async () => {
    if (!userId) {
      return;
    }

    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);
    setSyncJobInProgress(true);

    try {
      // Trigger sync operation
      const syncResult = await api.achievements.syncUserAchievements(userId);

      if (syncResult.success) {
        setSyncSuccess(`Successfully synced ${syncResult.gamesProcessed} games`);
        
        // Clear cache and re-fetch data
        clearCache(userId);
        await fetchAchievementStats(false);
      } else {
        // Display errors if any
        const errorMessages = syncResult.errors
          .map(e => `${e.gameId}: ${e.error}`)
          .join(', ');
        setSyncError(`Sync completed with errors: ${errorMessages}`);
      }
      setSyncJobInProgress(false);
    } catch (err: any) {
      console.error('Failed to sync achievements:', err);
      
      if (err.response?.status === 409) {
        setSyncError('Sync already in progress. Please wait...');
        // Keep checking status
        setSyncJobInProgress(true);
      } else if (err.response?.status === 429) {
        setSyncError('Rate limit exceeded. Please try again later.');
        setSyncJobInProgress(false);
      } else {
        setSyncError('Failed to sync achievements. Please try again.');
        setSyncJobInProgress(false);
      }
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Fetch data on mount
   * 
   */
  useEffect(() => {
    if (userId) {
      fetchAchievementStats();
    }
  }, [userId]);

  /**
   * Filter games by search query
   * 
   */
  const filteredGames = useMemo(() => {
    if (!stats) return [];

    let games = stats.perGameBreakdowns.filter(game => game.totalAchievements > 0);

    // Filter by search query (game name)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      games = games.filter(game => {
        const gameName = gameNames[game.gameId] || game.gameId;
        return gameName.toLowerCase().includes(query);
      });
    }

    return games;
  }, [stats, searchQuery, gameNames]);

  /**
   * Create game name lookup with fallback
   */
  const gameNamesWithFallback = useMemo(() => {
    const names: Record<string, string> = { ...gameNames };
    // Provide fallback for missing game names
    stats?.perGameBreakdowns.forEach(game => {
      if (!names[game.gameId]) {
        names[game.gameId] = `Game ${game.gameId.slice(0, 8)}`;
      }
    });
    return names;
  }, [gameNames, stats]);

  /**
   * Retry handler for error state
   * 
   */
  const handleRetry = () => {
    fetchAchievementStats(false);
  };

  /**
   * Handler to dismiss sync status messages
   */
  const handleDismissSyncMessage = () => {
    setSyncError(null);
    setSyncSuccess(null);
  };

  // No results after filtering
  const showNoResults = filteredGames.length === 0 && (stats?.perGameBreakdowns.length ?? 0) > 0;

  return (
    <div className={styles.achievements}>
      <SkeletonContainer
        loading={loading}
        skeleton={<AchievementsTabSkeleton />}
        label="achievements"
      >
        {/* Error state */}
        {error ? (
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>{error}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetry}
              aria-label="Retry loading achievements"
            >
              Retry
            </button>
          </div>
        ) : !stats || stats.totalAchievements === 0 ? (
          (syncing || syncJobInProgress) ? (
            <SyncProgress active label="achievements" />
          ) : (
            <EmptyState
              heading="No achievements found"
              description="Sync your gaming accounts to see your achievements"
            >
              {profile.isMe && (
                <SyncButton
                  loading={false}
                  onClick={handleSync}
                  disabled={false}
                  text="Sync Achievements"
                  ariaLabel="Sync achievements"
                />
              )}
              <SyncStatusMessage
                message={syncSuccess || ''}
                type="success"
                visible={!!syncSuccess}
                onDismiss={handleDismissSyncMessage}
                autoDismissMs={5000}
              />
              <SyncStatusMessage
                message={syncError || ''}
                type="error"
                visible={!!syncError}
                onDismiss={handleDismissSyncMessage}
                autoDismissMs={5000}
              />
            </EmptyState>
          )
        ) : (
          <>
            {/* Statistics Header */}
            <StatisticsHeader
              stats={stats}
              onSync={handleSync}
              syncing={syncing}
              isMe={!!profile.isMe}
            />

            {/* Sync progress indicator */}
            {(syncing || syncJobInProgress) && (
              <SyncProgress active label="achievements" />
            )}

            {/* Sync Status Messages */}
            <SyncStatusMessage
              message={syncSuccess || ''}
              type="success"
              visible={!!syncSuccess}
              onDismiss={handleDismissSyncMessage}
              autoDismissMs={5000}
            />
            <SyncStatusMessage
              message={syncError || ''}
              type="error"
              visible={!!syncError}
              onDismiss={handleDismissSyncMessage}
              autoDismissMs={5000}
            />

            {/* Search Filter */}
            <div className={styles.searchContainer}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search games"
              />
            </div>

            {/* Game Accordions */}
            <div className={styles.gameList}>
              {showNoResults ? (
                <div className={styles.noResults}>
                  <p>No games match your search</p>
                  <p className={styles.noResultsHint}>
                    Try adjusting your search criteria
                  </p>
                </div>
              ) : (
                filteredGames.map(game => (
                  <GameAccordion
                    key={game.gameId}
                    gameId={game.gameId}
                    gameName={gameNamesWithFallback[game.gameId]}
                    totalAchievements={game.totalAchievements}
                    unlockedCount={game.unlockedCount}
                    unlockPercentage={game.unlockPercentage}
                    userId={userId!}
                    coverUrl={gameCoverUrls[game.gameId]}
                  />
                ))
              )}
            </div>
          </>
        )}
      </SkeletonContainer>
    </div>
  );
};

export default Achievements;
